import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Pencil, Save, X, Pin, ShieldCheck, Trash2, FileText, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MarkdownView from "@/components/MarkdownView";
import MarkdownEditor from "@/components/MarkdownEditor";
import DocumentAttachments from "@/components/DocumentAttachments";
import { API_BASE_URL } from "@/config/api";
import { useAuth } from "@/config/auth";

const API = `${API_BASE_URL}/documents`;

export default function DocumentDetail() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [search, setSearch] = useSearchParams();
  const { isAdmin } = useAuth();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", category: "", visibility: "users", is_pinned: false });

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/${slug}`);
      setDoc(res.data);
      setForm({
        title: res.data.title,
        content: res.data.content || "",
        category: res.data.category,
        visibility: res.data.visibility,
        is_pinned: !!res.data.is_pinned,
      });
      // Auto-enter edit mode if ?edit=1 and user is admin
      if (search.get("edit") === "1" && res.data.can_edit) {
        setEditing(true);
        const next = new URLSearchParams(search); next.delete("edit"); setSearch(next, { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Document not found");
      navigate("/documents", { replace: true });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("Title cannot be empty"); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/${doc.id}`, {
        title: form.title,
        content: form.content,
        category: form.category,
        visibility: form.visibility,
        is_pinned: form.is_pinned,
      });
      setDoc(res.data);
      setEditing(false);
      toast.success("Document saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const handleCancelEdit = () => {
    setForm({
      title: doc.title, content: doc.content || "", category: doc.category,
      visibility: doc.visibility, is_pinned: !!doc.is_pinned,
    });
    setEditing(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/${doc.id}`);
      toast.success("Document deleted");
      navigate("/documents", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#43A047]" /></div>;
  if (!doc) return null;

  return (
    <div className="p-6 sm:p-8 max-w-5xl mx-auto" data-testid="document-detail">
      <button onClick={() => navigate("/documents")} className="text-xs text-[#617364] hover:text-[#43A047] flex items-center gap-1 mb-4" data-testid="doc-back">
        <ArrowLeft className="w-3.5 h-3.5" /> All Documents
      </button>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {doc.is_pinned && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#43A047]/12 text-[#388E3C] text-[10px] font-bold tracking-wider uppercase"><Pin className="w-2.5 h-2.5" /> Pinned</span>}
          <Badge variant="outline" className="border-[#43A047]/30 text-[#388E3C] text-[10px] uppercase tracking-wider font-bold">{doc.category}</Badge>
          {doc.visibility === "admin" && (
            <Badge variant="outline" className="border-[#7B1FA2]/30 text-[#7B1FA2] text-[10px] uppercase tracking-wider font-bold">Admin-only</Badge>
          )}
          {isAdmin && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider bg-[#1B291D] text-white"><ShieldCheck className="w-2.5 h-2.5" /> ADMIN</span>
          )}
        </div>

        {!editing ? (
          <h1 className="text-3xl sm:text-4xl tracking-tight font-light text-[#1B291D]" style={{ fontFamily: 'Work Sans' }} data-testid="doc-title">
            {doc.title}
          </h1>
        ) : (
          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="text-3xl sm:text-4xl font-light text-[#1B291D] border-[#E0E6E1] h-auto py-2 px-3"
            style={{ fontFamily: 'Work Sans' }}
            data-testid="doc-edit-title"
          />
        )}

        <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
          <div className="text-[11px] text-[#617364] flex items-center gap-3 tracking-wide">
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Updated {new Date(doc.updated_at).toLocaleString()}</span>
            {doc.updated_by && <span>· by {doc.updated_by}</span>}
          </div>
          {doc.can_edit && (
            <div className="flex items-center gap-2">
              {editing ? (
                <>
                  <Button variant="outline" onClick={handleCancelEdit} disabled={saving} className="border-[#E0E6E1] text-[#617364]" data-testid="doc-cancel">
                    <X className="w-3.5 h-3.5 mr-1" /> Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={saving} className="bg-[#43A047] hover:bg-[#388E3C] text-white" data-testid="doc-save">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
                  </Button>
                </>
              ) : (
                <>
                  {doc.slug !== "process-documentation" && (
                    <Button variant="outline" onClick={handleDelete} className="border-[#D32F2F]/40 text-[#D32F2F] hover:bg-[#D32F2F]/10" data-testid="doc-delete">
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                    </Button>
                  )}
                  <Button onClick={() => setEditing(true)} className="bg-[#1B291D] hover:bg-[#2a3d2e] text-white" data-testid="doc-edit">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      {editing ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Category</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 border-[#E0E6E1] bg-white" data-testid="doc-edit-category" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Visibility</Label>
              <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
                <SelectTrigger className="mt-1 border-[#E0E6E1] bg-white" data-testid="doc-edit-visibility"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="users">All logged-in users</SelectItem>
                  <SelectItem value="admin">Admins only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Pinned</Label>
              <div className="mt-1 flex items-center gap-2 h-9">
                <input
                  type="checkbox"
                  id="is_pinned"
                  checked={form.is_pinned}
                  onChange={(e) => setForm({ ...form, is_pinned: e.target.checked })}
                  className="w-4 h-4 accent-[#43A047]"
                  data-testid="doc-edit-pinned"
                />
                <label htmlFor="is_pinned" className="text-xs text-[#1B291D]">Show at the top of the list</label>
              </div>
            </div>
          </div>
          <MarkdownEditor value={form.content} onChange={(v) => setForm({ ...form, content: v })} minHeight={520} />
        </>
      ) : (
        <Card className="border-[#E0E6E1] bg-white p-6 sm:p-10" data-testid="doc-content">
          {doc.content ? <MarkdownView source={doc.content} /> : (
            <div className="text-center py-12">
              <FileText className="w-8 h-8 text-[#617364] mx-auto mb-2 opacity-40" />
              <p className="text-sm text-[#617364]">This document is empty.</p>
              {doc.can_edit && <p className="text-xs text-[#617364] mt-1">Click <strong className="text-[#1B291D]">Edit</strong> to add content.</p>}
            </div>
          )}
        </Card>
      )}

      <DocumentAttachments docId={doc.id} canEdit={!!doc.can_edit} />
    </div>
  );
}
